import { useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { METRIC_DICTIONARY_PATH, analyticsReportInfo, metricDefinition, type DrillRow, type ResolvedReportFilters } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDateTime, formatNumber } from '../../../lib/format';
import { router } from '../../../router';
import { useCanView } from '../../../stores/session';
import { DeepLink } from '../../notifications/components/DeepLink';
import { useAnalyticsDrill, type AnalyticsDrillUrl } from '../api/analytics-api';
import { useDrill, type DrillUrl } from '../api/reports-api';
import { periodText } from '../report-format';
import styles from '../Reports.module.css';

export type DrillPageUrl = DrillUrl & AnalyticsDrillUrl;

interface DrillView {
  readonly title: string;
  readonly count: number;
  readonly rows: readonly DrillRow[];
  readonly filters: ResolvedReportFilters;
  readonly dictionaryVersion: number;
  readonly definition: { readonly href: string; readonly name: string } | null;
}

/**
 * F17.02 — "every number opens its underlying records". Exactly the records one figure counted,
 * under the same filters (S28 QA 3), each opening its own screen: RPT-01's figures (`metric`) and
 * S29's (`report`).
 */
export function DrillPage(): ReactNode {
  const search: DrillPageUrl = useSearch({ from: '/app-layout/reports/drill' });
  const isAnalytics = search.report !== undefined;
  const canViewAll = useCanView('reports_all_staff');
  const canViewOwn = useCanView('reports_own');
  const canView = isAnalytics ? canViewOwn : canViewAll;
  const commandDrill = useDrill(search, canView && !isAnalytics);
  const analyticsDrill = useAnalyticsDrill(search, canView && isAnalytics);
  const query = isAnalytics ? analyticsDrill : commandDrill;
  const view = isAnalytics ? analyticsView(analyticsDrill.data) : commandView(commandDrill.data);

  if (!canView || query.error?.status === 403) return <ForbiddenState resource="these records" contact="Your admin" />;
  if (query.error && !view) return <ServerErrorState onRetry={() => void query.refetch()} />;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{view?.title ?? 'Records'}</h1>
          {view && (
            <p className={styles.lead}>
              {formatNumber(view.count)} {view.count === 1 ? 'record' : 'records'} · {periodText(view.filters)}
              {view.definition && (
                <>
                  {' · '}
                  <DeepLink to={view.definition.href} className={styles.inlineLink}>
                    {view.definition.name}, dictionary v{view.dictionaryVersion}
                  </DeepLink>
                </>
              )}
            </p>
          )}
        </div>
        <Button variant="outline" icon={ArrowLeft} onClick={() => router.history.back()}>
          Back to the report
        </Button>
      </header>

      <Card isFlush>
        {!view ? (
          <div className={styles.flushPad}>
            <SkeletonLines count={6} />
          </div>
        ) : view.rows.length === 0 ? (
          <EmptyState isInset title="No records" body="Nothing was counted for this figure under these filters. Go back and widen the period to see more." action={<Button variant="outline" icon={ArrowLeft} onClick={() => router.history.back()}>Back to the report</Button>} />
        ) : (
          <ul className={styles.rows}>
            {view.rows.map((row) => (
              <li key={row.id} className={`${styles.row} ${styles.drillRow}`}>
                <DeepLink to={row.href} className={styles.rowLinkBlock}>
                  <span className={styles.rowTitle}>{row.title}</span>
                  <span className={styles.cellMeta}>
                    {row.detail}
                    {row.at ? ` · ${formatDateTime(row.at)}` : ''}
                  </span>
                </DeepLink>
                {row.figure && <span className={styles.figure}>{row.figure}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {view && view.count > view.rows.length && <p className={styles.meta}>Showing the first {view.rows.length}.</p>}
    </div>
  );
}

function commandView(data: ReturnType<typeof useDrill>['data']): DrillView | null {
  if (!data) return null;
  return { title: data.title, count: data.count, rows: data.rows, filters: data.filters, dictionaryVersion: data.dictionaryVersion, definition: { href: `${METRIC_DICTIONARY_PATH}#${data.definitionKey}`, name: metricDefinition(data.definitionKey).name } };
}

function analyticsView(data: ReturnType<typeof useAnalyticsDrill>['data']): DrillView | null {
  if (!data) return null;
  return { title: `${analyticsReportInfo(data.report).name} — the records behind the figure`, count: data.count, rows: data.rows, filters: data.filters, dictionaryVersion: data.dictionaryVersion, definition: { href: METRIC_DICTIONARY_PATH, name: 'Metric definitions' } };
}
